import { Component, inject, input, output, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { getApiErrorMessage } from 'src/app/_helpers/api-error.helpers';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { CancellationFlowDetailsResponse } from 'src/app/_types/cancellation.types';
import { InsuranceFlowOption } from 'src/app/_types/insurance-flow.types';
import { LinkFlowModalComponent } from 'src/app/shared/components/link-flow-modal/link-flow-modal.component';
import { NoticeCardComponent } from 'src/app/shared/ui-kit/notice-card/notice-card.component';

import {
  CANCELLATION_LINK_TARGETS,
  CancellationLinkTarget,
  CancellationLinkTargetSpec,
} from './cancellation-link-target';

/**
 * One of the two flows a cancellation links — its `target` says which — as a
 * notice: what is linked and how far along it is, or an offer to link one.
 *
 * Takes the aggregate as an input rather than a mediator, so it renders the
 * same inside the cancellation modal and on the policy being cancelled. The
 * link is done here; the host only learns that the aggregate changed.
 */
@Component({
  selector: 'app-cancellation-link-card',
  standalone: true,
  imports: [NoticeCardComponent, LinkFlowModalComponent],
  template: `
    <app-notice-card
      [tone]="variant.tone"
      [title]="variant.title"
      [pill]="variant.pill"
      [pillClass]="variant.pillClass"
      [description]="variant.description"
      [actionLabel]="variant.actionLabel"
      [actionDisabled]="linking"
      (action)="onAction()"
    />
    <app-link-flow-modal
      #linkModal
      [title]="spec.modalTitle"
      [subtitle]="spec.modalSubtitle"
      [label]="spec.pickerLabel"
      [familyMemberId]="cancellation().memberId"
      [displayId]="counterpart?.displayId ?? ''"
      [excludeFlowId]="counterpart?.id ?? ''"
      [withPolicyOnly]="spec.withPolicyOnly"
      [purpose]="spec.purpose"
      [insuranceType]="cancellation().insuranceType"
      [busy]="linking"
      (picked)="link($event)"
    />
  `,
})
export class CancellationLinkCardComponent {
  private readonly cancellationService = inject(CancellationService);

  private readonly toastr = inject(ToastrService);

  readonly target = input.required<CancellationLinkTarget>();

  readonly cancellation = input.required<CancellationFlowDetailsResponse>();

  /** The refreshed aggregate, once the flow was linked. */
  readonly changed = output<CancellationFlowDetailsResponse>();

  /** "Voir l'affaire": the id of the linked flow, for the host to open. */
  readonly viewFlow = output<string>();

  @ViewChild('linkModal')
  private readonly linkModal?: LinkFlowModalComponent;

  protected linking = false;

  protected get spec(): CancellationLinkTargetSpec {
    return CANCELLATION_LINK_TARGETS[this.target()];
  }

  protected get variant() {
    return this.spec.variant(this.cancellation());
  }

  protected get counterpart() {
    return this.spec.counterpart(this.cancellation());
  }

  protected onAction(): void {
    const linked = this.spec.linked(this.cancellation());
    if (linked) this.viewFlow.emit(linked.id);
    else this.linkModal?.openModal();
  }

  protected link(option: InsuranceFlowOption): void {
    this.linking = true;
    this.spec
      .link(this.cancellationService, this.cancellation().id, option.id)
      .pipe(finalize(() => (this.linking = false)))
      .subscribe({
        next: (res) => {
          this.toastr.success(this.spec.successMessage);
          this.linkModal?.closeModal();
          this.changed.emit(res.data);
        },
        error: (err: unknown) => {
          this.toastr.error(
            getApiErrorMessage(
              err,
              $localize`Une erreur est survenue. Veuillez réessayer`
            )
          );
        },
      });
  }
}
